
import { supabase } from '@/integrations/supabase/client';
import { AnalyticsConfig } from './config';

export interface QueuedEvent {
  id: string;
  operation: 'track' | 'identify' | 'page' | 'pageleave' | 'alias';
  distinctId: string;
  event?: string;
  properties?: Record<string, any>;
  userProperties?: Record<string, any>;
  alias?: string;
  timestamp: number;
  retryCount: number;
}

export class EventQueue {
  private queue: QueuedEvent[] = [];
  private flushTimeout: NodeJS.Timeout | null = null;
  private config: AnalyticsConfig;

  constructor(config: AnalyticsConfig) {
    this.config = config;
  }

  add(event: Omit<QueuedEvent, 'id' | 'retryCount'>) {
    const queuedEvent: QueuedEvent = {
      ...event,
      id: crypto.randomUUID(),
      retryCount: 0,
    };

    this.queue.push(queuedEvent);
    this.scheduleFlush();
  }

  private scheduleFlush() {
    if (this.flushTimeout) clearTimeout(this.flushTimeout);
    this.flushTimeout = setTimeout(() => this.flush(), this.config.flushInterval);
  }

  async flush() {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.config.batchSize);
    
    for (const event of batch) {
      try {
        await this.sendEvent(event);
      } catch (error) {
        console.error('Failed to send analytics event:', error);
        await this.handleRetry(event);
      }
    }

    // Schedule next flush if there are more events
    if (this.queue.length > 0) {
      this.scheduleFlush();
    }
  }

  private async sendEvent(event: QueuedEvent) {
    // Send event without authentication since the edge function no longer requires it
    const { data, error } = await supabase.functions.invoke('posthog-analytics', {
      body: {
        operation: event.operation,
        distinctId: event.distinctId,
        event: event.event,
        properties: event.properties,
        userProperties: event.userProperties,
        alias: event.alias,
        timestamp: event.timestamp,
      }
    });

    if (error) {
      throw error;
    }

    return data;
  }

  private async handleRetry(event: QueuedEvent) {
    if (event.retryCount >= this.config.maxRetries) {
      console.warn('Max retries exceeded for analytics event, dropping:', event);
      return;
    }

    event.retryCount++;
    const delay = Math.min(
      this.config.retryDelay * Math.pow(2, event.retryCount - 1),
      this.config.maxRetryDelay
    );

    setTimeout(() => {
      this.queue.unshift(event); // Add back to front of queue
      this.scheduleFlush();
    }, delay);
  }

  getQueueSize(): number {
    return this.queue.length;
  }

  clear() {
    this.queue = [];
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
  }
}
